/**
 * MÓDULO DE KITS MAD (MADEIREIRA)
 * 
 * Este módulo implementa o gerenciamento de kits de produtos para madeireiras.
 * Um kit é um conjunto de produtos agrupados para venda conjunta.
 * O preço do kit é calculado automaticamente como o somatório dos produtos que o compõem.
 * 
 * CONFORMIDADE:
 * - Multi-tenancy por st_codHashEmpresa
 * - Autenticação e autorização obrigatórias
 * - Cálculos automáticos de preço baseado nos produtos do kit
 * - Suporte a múltiplos produtos com quantidades variáveis
 */

import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography, TableSortLabel,
  Tooltip, TablePagination, CircularProgress,
  FormControl, InputLabel, Select, MenuItem,
  Chip, Autocomplete, InputAdornment
} from "@mui/material";
import { 
  Add as AddIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Visibility as VisibilityIcon,
  Close as CloseIcon,
  Inventory as InventoryIcon,
  Calculate as CalculateIcon,
  Search as SearchIcon
} from "@mui/icons-material";
import api from "../../services/api";
import env from "../../config/env";
import AcessoRestrito from '../../components/AcessoRestrito';
import { verificarAcessoUsuario } from "../../utils/authStandards";
import { 
  tratarErroApi, 
  obterCodHashEmpresa 
} from "../../utils/crudUtils";
import { 
  formatarMoeda
} from "../../utils/formatadores";
import { useSystemMessagesContext } from "../../contexts/SystemMessagesContext";

export default function Kitmad() {
  const location = useLocation();
  const { addSuccess, addError, addWarning, addInfo } = useSystemMessagesContext();
  
  // Estados principais
  const [kits, setKits] = useState([]);
  const [totalKits, setTotalKits] = useState(0);
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [kitParaExcluir, setKitParaExcluir] = useState(null);
  const [openCalculo, setOpenCalculo] = useState(false);
  const [kitCalculo, setKitCalculo] = useState(null);
  const [resultadoCalculo, setResultadoCalculo] = useState(null);
  
  // Estados para formulário padrão
  const [openForm, setOpenForm] = useState(false);
  const [openView, setOpenView] = useState(false);
  const [kitSelecionado, setKitSelecionado] = useState(null);
  
  // Estados para paginação e filtros
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('st_nomeProduto');
  const [filtros, setFiltros] = useState({
    st_nomeProduto: ''
  });
  
  // Estados do formulário
  const [formData, setFormData] = useState({
    st_nomeKit: '', // Mantido para compatibilidade com backend (aceita st_nomeKit)
    st_descricao: '',
    itens: [],
    precoPersonalizado: null
  });
  const [formErrors, setFormErrors] = useState({});
  const [dataHoraAtual, setDataHoraAtual] = useState(new Date());

  // Carregar kits
  const carregarKits = async () => {
    try {
      setLoading(true);
      
      const token = localStorage.getItem('token');
      if (!token) {
        addError("Sessão expirada. Por favor, faça login novamente.");
        setKits([]);
        setLoading(false);
        return;
      }
      
      const timeoutPromise = new Promise((_, reject) => 
        setTimeout(() => reject(new Error('Timeout: A requisição demorou muito para responder')), 10000)
      );
      
      const res = await Promise.race([
        api.get('/kitmad', {
          params: {
            page: page + 1,
            limit: rowsPerPage,
            nome: filtros.st_nomeProduto || undefined,
            orderBy,
            order
          }
        }),
        timeoutPromise
      ]);
      
      // Backend retorna [{ kit: Produto, calculo: {...} }]
      // Onde kit é um Produto com bo_kit = 1
      if (Array.isArray(res.data?.dados)) {
        setKits(res.data.dados);
        setTotalKits(Number(res.data.total) || 0);
      } else if (Array.isArray(res.data)) {
        if (res.data.length === 0) {
          addWarning("Nenhum produto marcado como kit foi encontrado no banco de dados.");
        }
        
        // Garantir que cada item tenha a estrutura correta
        const kitsProcessados = res.data.map((item) => {
          // Se já tem a estrutura { kit, calculo }, mantém
          if (item.kit) {
            return item;
          }
          
          // Se é um produto direto, envolve na estrutura esperada
          if (item.produtoId || item.bo_kit === 1) {
            return { kit: item, calculo: {} };
          }
          
          return item;
        });
        
        setKits(kitsProcessados);
        setTotalKits(kitsProcessados.length);
      } else if (res.data && typeof res.data === 'object') {
        // Se retornou um único objeto
        if (res.data.kit) {
          setKits([res.data]);
          setTotalKits(1);
        } else if (res.data.produtoId || res.data.bo_kit === 1) {
          setKits([{ kit: res.data, calculo: {} }]);
          setTotalKits(1);
        } else {
          setKits([res.data]);
          setTotalKits(1);
        }
      } else {
        addError("Erro ao carregar kits: formato de resposta inválido");
        setKits([]);
        setTotalKits(0);
      }
    } catch (error) {
      console.error('Erro ao carregar kits:', error);
      tratarErroApi(error, addError, 'Carregar kits');
      setKits([]);
      setTotalKits(0);
    } finally {
      setLoading(false);
    }
  };

  // Carregar produtos para seleção
  const carregarProdutos = async () => {
    try {
      const res = await api.get('/prodmad/opcoes');
      if (Array.isArray(res.data)) {
        setProdutos(res.data);
      }
    } catch (error) {
      console.error("Erro ao carregar produtos:", error);
    }
  };

  useEffect(() => {
    carregarProdutos();
  }, []);

  useEffect(() => {
    const timer = setTimeout(carregarKits, 300);
    return () => clearTimeout(timer);
  }, [page, rowsPerPage, filtros, orderBy, order]);


  useEffect(() => {
    const timer = setInterval(() => {
      setDataHoraAtual(new Date());
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const formatarDataHora = (data) => {
    return data.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Funções para confirmação de exclusão
  const confirmarExclusao = (kit) => {
    setKitParaExcluir(kit);
    setOpenDeleteConfirm(true);
  };

  const fecharConfirmacaoExclusao = () => {
    setOpenDeleteConfirm(false);
    setKitParaExcluir(null);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: false
      }));
    }
  };

  const abrirFormulario = async (kit = null) => {
    setKitSelecionado(kit);
    
    if (kit) {
      const kitData = kit.kit || kit; // Backend retorna { kit: Produto, calculo: {...} }
      const kitId = kitData.produtoId || kit.produtoId;
      
      // Debug: verificar estrutura dos dados
      console.log('🔍 [kitmad] Abrindo formulário para edição:', {
        kitData,
        kitId,
        itensDoKit: kitData.itensDoKit,
        itens: kitData.itens,
        precoKit: kitData.precoKit
      });
      
      // Itens estão em itensDoKit (relacionamento Sequelize)
      let itensRaw = kitData.itensDoKit || kitData.itens || [];
      console.log('📦 [kitmad] Itens encontrados na listagem:', itensRaw.length, itensRaw);
      
      // Se não encontrou itens na listagem, busca o kit completo do servidor
      if ((!itensRaw || itensRaw.length === 0) && kitId) {
        console.log('⚠️ [kitmad] Itens não encontrados na listagem, buscando do servidor...');
        try {
          const response = await api.get(`/kitmad/${kitId}`);
          const kitCompleto = response.data;
          console.log('✅ [kitmad] Kit completo recebido do servidor:', {
            produtoId: kitCompleto.produtoId,
            itensDoKit: kitCompleto.itensDoKit?.length || 0,
            itens: kitCompleto.itens?.length || 0
          });
          
          // Atualiza os dados do kit com os dados completos do servidor
          itensRaw = kitCompleto.itensDoKit || kitCompleto.itens || [];
          
          // Atualiza também os outros dados se necessário
          if (kitCompleto.precoKit) {
            kitData.precoKit = kitCompleto.precoKit;
          }
        } catch (error) {
          console.error('❌ [kitmad] Erro ao buscar kit completo:', error);
          addError('Erro ao carregar dados completos do kit');
        }
      }
      
      console.log('📦 [kitmad] Itens finais para o formulário:', itensRaw.length, itensRaw);
      
      const itensMapeados = itensRaw.map((item, index) => {
        const produtoId = item.produtoId || item.produto?.produtoId || null;
        const produto = item.produto || null;
        
        // Garante que nu_quantidade seja sempre um número válido
        // Pode vir como Decimal do Sequelize, string, ou número
        let quantidade = item.nu_quantidade || item.quantidade;
        
        // Se for um objeto (Decimal do Sequelize), converte para número
        if (quantidade && typeof quantidade === 'object' && quantidade.toString) {
          quantidade = parseFloat(quantidade.toString());
        }
        
        // Converte para número se for string
        if (typeof quantidade === 'string') {
          quantidade = quantidade.trim() === '' ? 1 : parseFloat(quantidade);
        }
        
        // Converte para número se ainda não for
        if (typeof quantidade !== 'number') {
          quantidade = parseFloat(quantidade) || 1;
        }
        
        // Se for null, undefined ou NaN, usa 1 como padrão
        if (!quantidade || isNaN(quantidade) || quantidade <= 0) {
          quantidade = 1;
        }
        
        console.log(`  Item ${index + 1}: produtoId=${produtoId}, quantidade=${quantidade} (tipo: ${typeof quantidade})`, {
          item,
          nu_quantidade_raw: item.nu_quantidade,
          quantidade_raw: item.quantidade
        });
        
        return {
          produtoId: produtoId,
          produto: produto,
          nu_quantidade: quantidade
        };
      });
      
      console.log('✅ [kitmad] Itens mapeados:', itensMapeados);
      
      setFormData({
        st_nomeKit: kitData.st_nomeProduto || kitData.st_nomeKit || '', // Kit é produto, usa st_nomeProduto
        st_descricao: kitData.st_descricao || '',
        itens: itensMapeados,
        precoPersonalizado: kitData.precoKit?.vl_precoPersonalizado || null
      });
    } else {
      setFormData({
        st_nomeKit: '',
        st_descricao: '',
        itens: [],
        precoPersonalizado: null
      });
    }
    setOpenForm(true);
  };

  const fecharFormulario = () => {
    setOpenForm(false);
    setKitSelecionado(null);
    setFormData({
      st_nomeKit: '',
      st_descricao: '',
      itens: []
    });
    setFormErrors({});
  };

  const visualizarKit = async (kit) => {
    const kitData = kit.kit || kit;
    const kitId = kitData.produtoId || kitData.kitmadId || kitData.id || kitData.kitId;

    try {
      const response = await api.get(`/kitmad/${kitId}`);
      setKitSelecionado({ kit: response.data, calculo: kit.calculo || {} });
      setOpenView(true);
    } catch (error) {
      console.error('Erro ao carregar detalhes do kit:', error);
      tratarErroApi(error, addError, 'Carregar detalhes do kit');
    }
  };

  const fecharVisualizacao = () => {
    setOpenView(false);
    setKitSelecionado(null);
  };

  // Funções para gerenciar itens do kit
  const adicionarItem = () => {
    setFormData(prev => ({
      ...prev,
      itens: [...prev.itens, { produtoId: '', produto: null, nu_quantidade: 1 }]
    }));
  };

  const removerItem = (index) => {
    setFormData(prev => ({
      ...prev,
      itens: prev.itens.filter((_, i) => i !== index)
    }));
  };

  const atualizarItem = (index, campo, valor) => {
    setFormData(prev => ({
      ...prev,
      itens: prev.itens.map((item, i) => {
        if (i === index) {
          if (campo === 'produtoId') {
            const produto = produtos.find(p => p.produtoId === parseInt(valor));
            return { ...item, produtoId: valor, produto: produto || null };
          }
          return { ...item, [campo]: valor };
        }
        return item;
      })
    }));
  };

  const salvarKit = async () => {
    // Validação
    const errors = {};
    if (!formData.st_nomeKit.trim()) {
      errors.st_nomeKit = 'Nome do kit é obrigatório';
    }
    if (formData.itens.length === 0) {
      errors.itens = 'Adicione pelo menos um produto ao kit';
    }
    
    formData.itens.forEach((item, index) => {
      if (!item.produtoId) {
        errors[`item_${index}_produto`] = 'Selecione um produto';
      }
      // Verifica tanto nu_quantidade quanto quantidade (compatibilidade)
      const quantidadeStr = String(item.nu_quantidade || item.quantidade || '').trim();
      if (!quantidadeStr || quantidadeStr === '') {
        errors[`item_${index}_quantidade`] = 'Quantidade deve ser maior que zero';
      } else {
        const quantidade = parseFloat(quantidadeStr);
        if (isNaN(quantidade) || quantidade <= 0) {
          errors[`item_${index}_quantidade`] = 'Quantidade deve ser maior que zero';
        }
      }
    });

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      addWarning("Corrija os erros antes de salvar");
      return;
    }

    try {
      setLoading(true);

      const dadosFormatados = {
        st_nomeKit: formData.st_nomeKit.trim(),
        st_descricao: formData.st_descricao?.trim() || null,
        itens: formData.itens.map(item => ({
          produtoId: parseInt(item.produtoId), // Backend espera produtoId
          nu_quantidade: parseFloat(item.nu_quantidade || item.quantidade) || 1
        })),
        precoPersonalizado: formData.precoPersonalizado || null,
        st_codHashEmpresa: obterCodHashEmpresa()
      };


      if (kitSelecionado) {
        const kitData = kitSelecionado.kit || kitSelecionado;
        const kitId = kitData.produtoId || kitData.kitmadId || kitData.id || kitData.kitId; // Kit é produto, usa produtoId
        await api.put(`/kitmad/${kitId}`, dadosFormatados);
        addSuccess("Kit atualizado com sucesso!");
      } else {
        await api.post('/kitmad', dadosFormatados);
        addSuccess("Kit criado com sucesso!");
      }

      setOpenForm(false);
      setFormData({
        st_nomeKit: '',
        st_descricao: '',
        itens: [],
        precoPersonalizado: null
      });
      setFormErrors({});
      carregarKits();
    } catch (error) {
      console.error('❌ Erro ao salvar kit:', error);
      tratarErroApi(error, addError, 'Salvar kit');
    } finally {
      setLoading(false);
    }
  };

  const deletarKit = async () => {
    if (!kitParaExcluir) return;

    try {
      setLoading(true);
      const kitData = kitParaExcluir.kit || kitParaExcluir;
      const kitId = kitData.produtoId || kitData.kitmadId || kitData.id || kitData.kitId; // Kit é produto
      await api.delete(`/kitmad/${kitId}`);
      addSuccess("Kit excluído com sucesso!");
      carregarKits();
    } catch (error) {
      tratarErroApi(error, addError, 'Excluir kit');
    } finally {
      setLoading(false);
      fecharConfirmacaoExclusao();
    }
  };

  const calcularPreco = async () => {
    if (!kitCalculo) {
      addWarning("Selecione um kit para calcular");
      return;
    }

    try {
      setLoading(true);
      
      const kitData = kitCalculo.kit || kitCalculo;
      const kitId = kitData.produtoId || kitData.kitmadId || kitData.id || kitData.kitId; // Kit é produto
      
      if (!kitId) {
        addError("Kit inválido para cálculo");
        setLoading(false);
        return;
      }

      const response = await api.get(`/kitmad/${kitId}/calcular`);

      setResultadoCalculo(response.data);
      addSuccess("Cálculo realizado com sucesso!");
    } catch (error) {
      console.error('Erro ao calcular preço:', error);
      tratarErroApi(error, addError, 'Calcular preço do kit');
    } finally {
      setLoading(false);
    }
  };

  const abrirCalculo = (kit) => {
    setKitCalculo(kit);
    setResultadoCalculo(null);
    setOpenCalculo(true);
  };

  const fecharCalculo = () => {
    setOpenCalculo(false);
    setKitCalculo(null);
    setResultadoCalculo(null);
  };

  const handleFiltroChange = (event) => {
    const { name, value } = event.target;
    setFiltros(prev => ({
      ...prev,
      [name]: value
    }));
    setPage(0);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const kitsPaginados = kits;

  // Verificar acesso do usuário
  if (!verificarAcessoUsuario("prodmad")) {
    return <AcessoRestrito />;
  }

  return (
    <AcessoRestrito modulo="prodmad">
      <Box
        sx={{
          height: '100%',
          width: '100%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Box interno com margens de 1,5% */}
        <Box sx={{
          margin: '0 1.5% 1.5% 1.5%',
          height: '90vh',
          display: 'flex',
          flexDirection: 'column'
        }}>

          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: { xs: 2, sm: 3 },
              flexDirection: { xs: 'column', sm: 'row' },
              gap: 2
            }}
          >
            <Typography 
              variant="h5" 
              mb={1} 
              sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <InventoryIcon sx={{ 
                fontSize: { xs: '1.5rem', sm: '2rem' },
                color: '#FFC107'
              }} />
              Kits MAD
            </Typography>
            <Button 
              variant="contained" 
              onClick={() => abrirFormulario()}
              sx={{
                width: { xs: '100%', sm: 'auto' },
                minWidth: { xs: 'auto', sm: '200px' },
                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                  ? '#ffffff' 
                  : theme.palette.primary.main,
                color: (theme) => theme.palette.mode === 'dark' 
                  ? '#000000' 
                  : theme.palette.primary.contrastText,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? '#f5f5f5' 
                    : theme.palette.primary.dark
                }
              }}
            >
              <AddIcon /> Novo Kit
            </Button>
          </Box>

          {/* Container da Tabela */}
          <TableContainer 
            component={Paper} 
            sx={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'auto',
              height: {
                xs: 'calc(90vh - 140px)',
                sm: 'calc(90vh - 120px)',
                md: 'calc(90vh - 120px)',
                lg: 'calc(90vh - 120px)'
              },
              maxHeight: {
                xs: 'calc(90vh - 140px)',
                sm: 'calc(90vh - 120px)',
                md: 'calc(90vh - 120px)',
                lg: 'calc(90vh - 120px)'
              },
              width: '100%',
              minWidth: { xs: '100%', sm: '600px' },
              maxWidth: '100%',
              boxShadow: (theme) => theme.palette.mode === 'dark' 
                ? '0 2px 8px rgba(0, 0, 0, 0.15)' 
                : '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: 2,
              border: 'none'
            }}
          >
            <Table sx={{ minWidth: '100%', tableLayout: 'fixed' }}>
              <TableHead sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: (theme) => theme.palette.background.paper,
                '& .MuiTableCell-root': {
                  backgroundColor: 'inherit',
                  borderBottom: '1px solid rgba(224, 224, 224, 1)'
                }
              }}>
                <TableRow>
                  <TableCell sx={{ 
                    width: { xs: '30%', sm: '25%' },
                    minWidth: { xs: '150px', sm: '120px' }
                  }}>
                    <TableSortLabel
                      active={orderBy === "st_nomeProduto"}
                      direction={orderBy === "st_nomeProduto" ? order : "asc"}
                      onClick={() => handleRequestSort("st_nomeProduto")}
                    >
                      Nome do Kit
                    </TableSortLabel>
                    <TextField
                      name="st_nomeProduto"
                      size="small"
                      fullWidth
                      value={filtros.st_nomeProduto || ''}
                      onChange={handleFiltroChange}
                      placeholder="Filtrar por nome"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        )
                      }}
                      sx={{ 
                        mt: 1,
                        '& .MuiInputBase-input': {
                          fontSize: { xs: '0.75rem', sm: '0.875rem' }
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ 
                    width: { xs: '25%', sm: '30%' },
                    minWidth: { xs: '120px', sm: '150px' }
                  }}>
                    Descrição
                  </TableCell>
                  <TableCell sx={{ 
                    width: { xs: '15%', sm: '12%' },
                    minWidth: { xs: '100px', sm: '80px' }
                  }}>
                    Itens
                  </TableCell>
                  <TableCell sx={{ 
                    width: { xs: '15%', sm: '15%' },
                    minWidth: { xs: '100px', sm: '100px' }
                  }}>
                    Valor Total
                  </TableCell>
                  <TableCell sx={{ 
                    width: { xs: '15%', sm: '18%' },
                    minWidth: { xs: '100px', sm: '120px' },
                    textAlign: 'center'
                  }}>
                    Ações
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : kitsPaginados.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                        <InventoryIcon sx={{ fontSize: 48, color: 'text.secondary', opacity: 0.5 }} />
                        <Typography variant="body1" color="text.secondary" fontWeight={500}>
                          Nenhum kit encontrado
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {kits.length === 0 
                            ? "Não há produtos marcados como kit no sistema. Clique em 'Novo Kit' para criar um."
                            : "Nenhum kit corresponde aos filtros aplicados. Tente ajustar os filtros."}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  kitsPaginados.map((item, index) => {
                    // Garantir estrutura correta: { kit: Produto, calculo: {...} }
                    const kit = item.kit || (item.produtoId ? item : {});
                    const calculo = item.calculo || {};
                    const kitId = kit.produtoId || kit.kitmadId || kit.id || kit.kitId || `kit-${index}`;
                    // Itens do kit estão em itensDoKit (relacionamento Sequelize)
                    const itens = kit.itensDoKit || kit.itens || [];
                    const quantidadeItens = item.quantidadeItens ?? itens.length;
                    
                    return (
                      <TableRow 
                        key={kitId}
                        sx={{
                          backgroundColor: (theme) => 
                            index % 2 === 0 
                              ? theme.palette.mode === 'dark' 
                                ? 'rgba(255, 255, 255, 0.02)' 
                                : 'rgba(0, 0, 0, 0.02)'
                              : 'transparent',
                          '&:hover': {
                            backgroundColor: (theme) => 
                              theme.palette.mode === 'dark' 
                                ? 'rgba(255, 255, 255, 0.08)' 
                                : 'rgba(0, 0, 0, 0.04)'
                          }
                        }}
                      >
                        <TableCell 
                          sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            height: '40px',
                            padding: '8px 16px',
                            verticalAlign: 'middle'
                          }}
                        >
                          {kit.st_nomeProduto || kit.st_nomeKit || '-'}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            overflow: 'hidden', 
                            textOverflow: 'ellipsis', 
                            whiteSpace: 'nowrap',
                            height: '40px',
                            padding: '8px 16px',
                            verticalAlign: 'middle'
                          }}
                        >
                          {kit.st_descricao || '-'}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            height: '40px',
                            padding: '8px 16px',
                            verticalAlign: 'middle'
                          }}
                        >
                          {quantidadeItens > 0 ? (
                            <Chip 
                              label={`${quantidadeItens} produto${quantidadeItens !== 1 ? 's' : ''}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          ) : (
                            <Chip 
                              label="0 produtos"
                              size="small"
                              color="default"
                              variant="outlined"
                            />
                          )}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            height: '40px',
                            padding: '8px 16px',
                            verticalAlign: 'middle',
                            fontWeight: 500,
                            color: calculo.totalFinal ? 'inherit' : 'text.secondary'
                          }}
                        >
                          {calculo && calculo.totalFinal !== undefined && calculo.totalFinal !== null
                            ? formatarMoeda(calculo.totalFinal)
                            : '-'}
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            height: '40px',
                            padding: '8px 16px',
                            verticalAlign: 'middle',
                            textAlign: 'center'
                          }}
                        >
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                            <Tooltip title="Calcular Preço">
                              <IconButton
                                size="small"
                                onClick={() => abrirCalculo(item)}
                                sx={{
                                  color: 'primary.main',
                                  '&:hover': {
                                    backgroundColor: 'rgba(25, 118, 210, 0.1)'
                                  }
                                }}
                              >
                                <CalculateIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Visualizar">
                              <IconButton
                                size="small"
                                onClick={() => visualizarKit(item)}
                                sx={{
                                  color: 'info.main',
                                  '&:hover': {
                                    backgroundColor: 'rgba(33, 150, 243, 0.1)'
                                  }
                                }}
                              >
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Editar">
                              <IconButton
                                size="small"
                                onClick={() => abrirFormulario(item)}
                                sx={{
                                  color: 'warning.main',
                                  '&:hover': {
                                    backgroundColor: 'rgba(255, 193, 7, 0.1)'
                                  }
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Excluir">
                              <IconButton
                                size="small"
                                onClick={() => confirmarExclusao(item)}
                                sx={{
                                  color: 'error.main',
                                  '&:hover': {
                                    backgroundColor: 'rgba(211, 47, 47, 0.1)'
                                  }
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Paginação */}
          <Paper
            elevation={1}
            sx={{
              mt: 2,
              p: 1,
              backgroundColor: (theme) => theme.palette.mode === 'dark' 
                ? 'rgba(255, 255, 255, 0.05)' 
                : 'rgba(255, 255, 255, 0.8)',
              borderRadius: 2
            }}
          >
            <TablePagination
              component="div"
              count={totalKits}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              labelRowsPerPage="Itens por página"
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
              sx={{
                '& .MuiTablePagination-toolbar': {
                  flexWrap: 'wrap',
                  gap: 1
                },
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }
              }}
            />
          </Paper>
        </Box>

        {/* Dialog de Formulário */}
        <Dialog
          open={openForm}
          onClose={fecharFormulario}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              width: { xs: '95vw', sm: '90vw', md: '900px' },
              height: { xs: '95vh', sm: '90vh', md: '550px' },
              maxWidth: { xs: '95vw', sm: '90vw', md: '900px' },
              maxHeight: { xs: '95vh', sm: '90vh', md: '550px' },
              position: 'relative',
              margin: { xs: 'auto', sm: 'auto', md: 'auto' }
            }
          }}
        >
          <DialogTitle sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            fontSize: '1.25rem',
            fontWeight: 500
          }}>
            {kitSelecionado ? "Editar Kit" : "Novo Kit"}
          </DialogTitle>
          <DialogContent sx={{ 
            height: { xs: 'calc(100% - 120px)', sm: 'calc(100% - 140px)' },
            overflow: 'auto',
            padding: { xs: 1, sm: 2 },
            '&.MuiDialogContent-root': {
              paddingTop: { xs: 1, sm: 2 }
            },
            position: 'relative',
            top: 0,
            marginBottom: { xs: '60px', sm: '60px' },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            },
            '& .MuiInputBase-input': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            },
            '& .MuiFormControlLabel-label': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                fullWidth
                label="Nome do Kit *"
                name="st_nomeKit"
                value={formData.st_nomeKit}
                onChange={handleInputChange}
                error={!!formErrors.st_nomeKit}
                helperText={formErrors.st_nomeKit}
                required
              />
              <TextField
                fullWidth
                label="Descrição"
                name="st_descricao"
                value={formData.st_descricao}
                onChange={handleInputChange}
                multiline
                rows={3}
              />
              
              {/* Seção de Itens */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1">Produtos do Kit</Typography>
                  <Button size="small" onClick={adicionarItem} startIcon={<AddIcon />}>
                    Adicionar Produto
                  </Button>
                </Box>
                {formErrors.itens && (
                  <Typography color="error" variant="caption">
                    {formErrors.itens}
                  </Typography>
                )}
                {formData.itens.map((item, index) => (
                  <Box key={index} sx={{ display: 'flex', gap: 2, alignItems: 'center', p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Autocomplete
                      options={produtos}
                      getOptionLabel={(option) => option.st_nomeProduto || ''}
                      value={item.produto || produtos.find(p => p.produtoId === parseInt(item.produtoId)) || null}
                      onChange={(event, newValue) => {
                        atualizarItem(index, 'produtoId', newValue ? newValue.produtoId : '');
                        atualizarItem(index, 'produto', newValue);
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Produto *"
                          size="small"
                          error={!!formErrors[`item_${index}_produto`]}
                          helperText={formErrors[`item_${index}_produto`]}
                          sx={{ flex: 2 }}
                        />
                      )}
                      sx={{ flex: 2 }}
                    />
                    <TextField
                      label="Quantidade *"
                      type="number"
                      value={item.nu_quantidade || item.quantidade || ''}
                      onChange={(e) => atualizarItem(index, 'nu_quantidade', e.target.value)}
                      size="small"
                      error={!!formErrors[`item_${index}_quantidade`]}
                      helperText={formErrors[`item_${index}_quantidade`]}
                      inputProps={{ step: "0.0001", min: "0.0001" }}
                      sx={{ flex: 1 }}
                    />
                    <IconButton onClick={() => removerItem(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                {formData.itens.length === 0 && (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    Nenhum produto adicionado. Clique em "Adicionar Produto" para começar.
                  </Typography>
                )}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{
            padding: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider',
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            justifyContent: 'space-between'
          }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {kitSelecionado && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={() => confirmarExclusao(kitSelecionado)}
                  startIcon={<DeleteIcon />}
                  disabled={loading}
                >
                  EXCLUIR
                </Button>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={fecharFormulario}
              >
                FECHAR
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={salvarKit}
                disabled={loading}
                startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
              >
                {loading ? "SALVANDO..." : "SALVAR"}
              </Button>
            </Box>
          </DialogActions>
        </Dialog>

        {/* Dialog de Visualização */}
        <Dialog
          open={openView}
          onClose={fecharVisualizacao}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              width: { xs: '95vw', sm: '90vw', md: '900px' },
              height: { xs: '95vh', sm: '90vh', md: '550px' },
              maxWidth: { xs: '95vw', sm: '90vw', md: '900px' },
              maxHeight: { xs: '95vh', sm: '90vh', md: '550px' },
              position: 'relative',
              margin: { xs: 'auto', sm: 'auto', md: 'auto' }
            }
          }}
        >
          <DialogTitle sx={{
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            fontSize: '1.25rem',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            Visualizar Kit
            <IconButton onClick={fecharVisualizacao}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ 
            height: { xs: 'calc(100% - 120px)', sm: 'calc(100% - 140px)' },
            overflow: 'auto',
            padding: { xs: 1, sm: 2 },
            '&.MuiDialogContent-root': {
              paddingTop: { xs: 1, sm: 2 }
            },
            position: 'relative',
            top: 0,
            marginBottom: { xs: '60px', sm: '60px' },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            },
            '& .MuiInputBase-input': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }
          }}>
            {kitSelecionado && (() => {
              const kitData = kitSelecionado.kit || kitSelecionado;
              const calculo = kitSelecionado.calculo || {};
              const itens = kitData.itensDoKit || kitData.itens || [];
              
              return (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Nome do Kit"
                    value={kitData.st_nomeProduto || kitData.st_nomeKit || ''}
                    fullWidth
                    InputProps={{ readOnly: true }}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: 'rgba(0, 0, 0, 0.6)',
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  />
                  <TextField
                    label="Descrição"
                    value={kitData.st_descricao || ''}
                    fullWidth
                    multiline
                    rows={3}
                    InputProps={{ readOnly: true }}
                    sx={{
                      '& .MuiInputBase-input': {
                        color: 'rgba(0, 0, 0, 0.6)',
                        WebkitTextFillColor: 'rgba(0, 0, 0, 0.6)',
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                      }
                    }}
                  />
                  
                  <Typography variant="subtitle1" sx={{ mt: 2 }}>
                    Produtos do Kit
                  </Typography>
                  {itens.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Produto</TableCell>
                            <TableCell align="right">Quantidade</TableCell>
                            {calculo.itens && calculo.itens.length > 0 && (
                              <>
                                <TableCell align="right">Tipo</TableCell>
                                <TableCell align="right">Valor Unit.</TableCell>
                                <TableCell align="right">Subtotal</TableCell>
                              </>
                            )}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {itens.map((item, index) => {
                            const produto = item.produto || {};
                            const calculoItem = calculo.itens?.[index];
                            return (
                              <TableRow key={index}>
                                <TableCell>{produto.st_nomeProduto || '-'}</TableCell>
                                <TableCell align="right">{item.nu_quantidade || item.quantidade || '-'}</TableCell>
                                {calculoItem && (
                                  <>
                                    <TableCell align="right">{calculoItem.calculo?.tipo || '-'}</TableCell>
                                    <TableCell align="right">
                                      {calculoItem.calculo?.preco_unitario ? formatarMoeda(calculoItem.calculo.preco_unitario) : '-'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {calculoItem.calculo?.valor_final ? formatarMoeda(calculoItem.calculo.valor_final) : '-'}
                                    </TableCell>
                                  </>
                                )}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                      Nenhum produto cadastrado no kit.
                    </Typography>
                  )}
                  
                  {calculo.totalFinal !== undefined && (
                    <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(25, 118, 210, 0.1)', borderRadius: 1 }}>
                      <Typography variant="h6" color="primary.main">
                        Valor Total do Kit: {formatarMoeda(calculo.totalFinal)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })()}
          </DialogContent>
          <DialogActions sx={{
            padding: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider',
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            justifyContent: 'flex-end'
          }}>
            <Button onClick={fecharVisualizacao} variant="contained">
              FECHAR
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de Cálculo */}
        <Dialog
          open={openCalculo}
          onClose={fecharCalculo}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              width: { xs: '95vw', sm: '90vw', md: '500px' },
              maxWidth: { xs: '95vw', sm: '90vw', md: '500px' },
              position: 'relative',
              margin: { xs: 'auto', sm: 'auto', md: 'auto' }
            }
          }}
        >
          <DialogTitle sx={{
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            fontSize: '1.25rem',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalculateIcon />
              Calcular Preço do Kit
            </Box>
            <IconButton onClick={fecharCalculo} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ 
            padding: { xs: 1, sm: 2 },
            '&.MuiDialogContent-root': {
              paddingTop: { xs: 1, sm: 2 }
            },
            '& .MuiInputLabel-root': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            },
            '& .MuiInputBase-input': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }
          }}>
            {kitCalculo && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  label="Kit"
                  value={(kitCalculo.kit || kitCalculo).st_nomeProduto || (kitCalculo.kit || kitCalculo).st_nomeKit || ''}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  sx={{
                    '& .MuiInputBase-input': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                />
                <Button
                  variant="contained"
                  onClick={calcularPreco}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} /> : <CalculateIcon />}
                  fullWidth
                >
                  {loading ? 'Calculando...' : 'Calcular'}
                </Button>
                {resultadoCalculo && (
                  <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1, mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Resultado do Cálculo
                    </Typography>
                    {resultadoCalculo.itens && resultadoCalculo.itens.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        {resultadoCalculo.itens.map((item, index) => (
                          <Box key={index} sx={{ mb: 2, p: 1.5, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
                            <Typography variant="body2" fontWeight="bold" sx={{ mb: 0.5 }}>
                              {item.nome || item.produto || `Produto ${index + 1}`}
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 0.5 }}>
                              <strong>Quantidade:</strong> {item.quantidade || item.nu_quantidade || '-'}
                            </Typography>
                            {item.calculo && (
                              <>
                                {item.calculo.tipo && (
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Tipo:</strong> {item.calculo.tipo}
                                  </Typography>
                                )}
                                {item.calculo.calculo && (
                                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    <strong>Medida:</strong> {item.calculo.calculo} {item.calculo.tipo || ''}
                                  </Typography>
                                )}
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                  <strong>Preço Unitário:</strong> {item.calculo.preco_unitario && item.calculo.preco_unitario > 0 ? formatarMoeda(item.calculo.preco_unitario) : 'R$ 0,00'}
                                </Typography>
                                <Typography variant="body2" fontWeight="bold" sx={{ color: 'primary.main' }}>
                                  <strong>Subtotal:</strong> {item.subtotal && item.subtotal > 0 ? formatarMoeda(item.subtotal) : (item.calculo?.valor_final && item.calculo.valor_final > 0 ? formatarMoeda(item.calculo.valor_final) : 'R$ 0,00')}
                                </Typography>
                              </>
                            )}
                            {item.calculo?.error && (
                              <Box sx={{ mt: 1, p: 1, backgroundColor: 'rgba(255, 193, 7, 0.1)', borderRadius: 1 }}>
                                <Typography variant="caption" color="warning.main">
                                  ⚠️ {item.calculo.error}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}
                    <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(25, 118, 210, 0.1)', borderRadius: 1 }}>
                      <Typography variant="h6" sx={{ color: resultadoCalculo.totalFinal && resultadoCalculo.totalFinal > 0 ? 'primary.main' : 'error.main' }}>
                        <strong>Valor Total:</strong> {resultadoCalculo.totalFinal && resultadoCalculo.totalFinal > 0 ? formatarMoeda(resultadoCalculo.totalFinal) : 'R$ 0,00'}
                      </Typography>
                    </Box>
                    {(!resultadoCalculo.totalFinal || resultadoCalculo.totalFinal === 0) && (
                      <Box sx={{ mt: 2, p: 2, backgroundColor: 'rgba(255, 193, 7, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" color="warning.main">
                          ⚠️ Atenção: O kit não possui produtos com preço cadastrado. Verifique se os produtos do kit possuem unidades com preço na aba "Unidades" do formulário de edição.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{
            padding: 2,
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider',
            position: 'sticky',
            bottom: 0,
            zIndex: 1,
            justifyContent: 'flex-end'
          }}>
            <Button onClick={fecharCalculo} variant="contained">
              FECHAR
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog de Confirmação de Exclusão */}
        <Dialog open={openDeleteConfirm} onClose={fecharConfirmacaoExclusao} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderBottom: 1,
            borderColor: 'divider',
            padding: 2,
            fontSize: '1.25rem',
            fontWeight: 500
          }}>
            Confirmar Exclusão
          </DialogTitle>
          <DialogContent sx={{ padding: 2 }}>
            <Typography>
              Tem certeza que deseja excluir este kit?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ 
            backgroundColor: (theme) => theme.palette.mode === 'dark' ? '#1B5E20' : '#E0F8E0',
            borderTop: 1,
            borderColor: 'divider',
            padding: 2
          }}>
            <Button onClick={fecharConfirmacaoExclusao}>CANCELAR</Button>
            <Button 
              variant="contained" 
              color="error" 
              onClick={deletarKit} 
              disabled={loading}
            >
              EXCLUIR
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </AcessoRestrito>
  );
}
